import { useTable } from "@refinedev/core";
import { Link, useSearchParams } from "react-router";

import { Badge } from "@/components/Badge";
import { ErrorBox, FilterBar, Loading, PageHeader, Pagination, Select } from "@/components/Page";
import { EmptyRow, Sub, Table, Td, Th, Tr } from "@/components/Table";
import {
  INTERVIEW_STATUS_OPTIONS,
  formatDateTime,
  formatDuration,
  interviewStatus,
  voiceProviderLabel,
} from "@/lib/format";
import { filterValue } from "@/lib/query";

export interface InterviewCallRow extends Record<string, unknown> {
  id: string;
  memoir_id: string;
  memoir_title: string | null;
  storyteller_name: string | null;
  prompt_delivery_id: string | null;
  question: string | null;
  status: string;
  attempt: number;
  provider: string;
  provider_conversation_id: string | null;
  scheduled_at: string;
  started_at: string | null;
  ended_at: string | null;
  duration_s: number | null;
  failure_reason: string | null;
  story_id: string | null;
}

export function InterviewCallsPage() {
  // Przegląd linkuje tu z gotowym filtrem ("rozmowy, które nie ruszyły"),
  // a detal Opowieści z jej identyfikatorem - adres jest stanem tabeli.
  const [searchParams] = useSearchParams();
  const zAdresu = (["status", "memoir_id"] as const)
    .filter((field) => searchParams.get(field))
    .map((field) => ({ field, operator: "eq" as const, value: searchParams.get(field)! }));

  const { result, tableQuery, filters, setFilters, currentPage, setCurrentPage, pageSize } =
    useTable<InterviewCallRow>({
      resource: "interview-calls",
      sorters: { initial: [{ field: "scheduled_at", order: "desc" }] },
      filters: { initial: zAdresu.length > 0 ? zAdresu : undefined },
    });

  const ustawFiltr = (field: string, value: string) =>
    setFilters([{ field, operator: "eq", value }], "merge");

  return (
    <>
      <PageHeader
        title="Rozmowy telefoniczne"
        description="Każdy telefon do narratora: kiedy miał wyjść, czy się odbył i co poszło nie tak. Rozmowa bez potwierdzenia to nie awaria - to znak, że nie wiemy, czy telefon zadzwonił."
      />

      <FilterBar>
        <Select
          label="Status"
          value={filterValue(filters, "status")}
          options={INTERVIEW_STATUS_OPTIONS}
          onChange={(value) => ustawFiltr("status", value)}
        />
        <label className="flex flex-col gap-1 text-base text-ink-soft">
          Zaplanowane od
          <input
            type="date"
            value={filterValue(filters, "scheduled_from").slice(0, 10)}
            onChange={(event) =>
              ustawFiltr(
                "scheduled_from",
                event.target.value ? `${event.target.value}T00:00:00Z` : "",
              )
            }
            className="rounded-card border border-line bg-surface px-3 py-2 text-ink"
          />
        </label>
        <label className="flex flex-col gap-1 text-base text-ink-soft">
          do
          <input
            type="date"
            value={filterValue(filters, "scheduled_to").slice(0, 10)}
            onChange={(event) =>
              ustawFiltr("scheduled_to", event.target.value ? `${event.target.value}T23:59:59Z` : "")
            }
            className="rounded-card border border-line bg-surface px-3 py-2 text-ink"
          />
        </label>
      </FilterBar>

      <ErrorBox error={tableQuery.error} />

      {tableQuery.isLoading ? (
        <Loading />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Zaplanowana</Th>
                <Th>Opowieść</Th>
                <Th>Pytanie</Th>
                <Th>Status</Th>
                <Th align="right">Próba</Th>
                <Th align="right">Czas</Th>
                <Th>Powód</Th>
                <Th>Historia</Th>
              </tr>
            </thead>
            <tbody>
              {result.data.length === 0 && (
                <EmptyRow colSpan={8}>Żadna rozmowa nie pasuje do tych filtrów.</EmptyRow>
              )}
              {result.data.map((call) => {
                const status = interviewStatus(call.status);
                return (
                  <Tr key={call.id}>
                    <Td nowrap>
                      <Link
                        to={`/rozmowy/${call.id}`}
                        className="text-accent underline underline-offset-4 hover:text-accent-dark"
                      >
                        {formatDateTime(call.scheduled_at)}
                      </Link>
                      <Sub>{voiceProviderLabel(call.provider)}</Sub>
                    </Td>
                    <Td nowrap>
                      {call.memoir_title ?? "-"}
                      {call.storyteller_name && <Sub>{call.storyteller_name}</Sub>}
                    </Td>
                    <Td>{call.question ?? "rozmowa poza pytaniem tygodnia"}</Td>
                    <Td>
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </Td>
                    <Td align="right" numeric>
                      {call.attempt}
                    </Td>
                    <Td align="right" numeric nowrap>
                      {formatDuration(call.duration_s)}
                    </Td>
                    <Td>
                      <span className="text-ink-soft">{call.failure_reason ?? "-"}</span>
                    </Td>
                    <Td nowrap>
                      {call.story_id ? (
                        <Link
                          to={`/historie/${call.story_id}`}
                          className="text-accent underline underline-offset-4 hover:text-accent-dark"
                        >
                          Powstała
                        </Link>
                      ) : (
                        <span className="text-ink-soft">-</span>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>

          <Pagination
            page={currentPage}
            pageSize={pageSize}
            total={result.total ?? 0}
            onChange={setCurrentPage}
          />
        </>
      )}
    </>
  );
}
